import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { LogIn, Mail, Lock, AlertCircle, Shield } from 'lucide-react';

type LoginFormData = {
  email: string;
  password: string;
};

const LoginPage: React.FC = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rawRedirect = params.get('redirect');
  const safeRedirect = rawRedirect !== null && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')
    ? rawRedirect
    : '/admin';
  
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await signIn(data.email, data.password);
      
      if (error) {
        setError(error.message);
      } else {
        navigate(safeRedirect);
      }
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block relative w-1/2">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80"
          alt="Admin Dashboard"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 to-neutral-900/50 mix-blend-multiply" />
        <div className="absolute inset-0 flex flex-col justify-center px-12">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-12 w-12 rounded-lg bg-[#0066FF] flex items-center justify-center">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">
              Administration
            </h1>
          </div>
          <p className="text-xl text-neutral-200 mb-8">
            Accès administrateur au centre de support Autoquote
          </p>
          <div className="space-y-6 text-neutral-300">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-[#0066FF]/20 flex items-center justify-center">
                <span className="text-lg">🎯</span>
              </div>
              <p className="ml-4">Gestion du CRM et du pipeline commercial</p>
            </div>
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-[#0066FF]/20 flex items-center justify-center">
                <span className="text-lg">📚</span>
              </div>
              <p className="ml-4">Administration de la base de connaissances</p>
            </div>
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-[#0066FF]/20 flex items-center justify-center">
                <span className="text-lg">👥</span>
              </div>
              <p className="ml-4">Gestion des utilisateurs et des droits</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="h-12 w-12 rounded-lg bg-[#0066FF] flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900">
              Accès Administrateur
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              Console d'administration du support Autoquote
            </p>
          </div>

          {error && (
            <div className="mt-6 flex items-center p-4 border border-red-100 rounded-lg bg-red-50">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700">
                Email administrateur
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-neutral-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`pl-10 block w-full rounded-lg border ${
                    errors.email ? 'border-red-300' : 'border-neutral-300'
                  } px-3 py-2 text-neutral-900 placeholder-neutral-500 focus:border-[#0066FF] focus:ring-[#0066FF]/20 sm:text-sm`}
                  placeholder="vous@entreprise.fr"
                  {...register('email', {
                    required: 'L\'email est requis',
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: 'Veuillez entrer une adresse email valide',
                    },
                  })}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700">
                Mot de passe
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-neutral-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className={`pl-10 block w-full rounded-lg border ${
                    errors.password ? 'border-red-300' : 'border-neutral-300'
                  } px-3 py-2 text-neutral-900 placeholder-neutral-500 focus:border-[#0066FF] focus:ring-[#0066FF]/20 sm:text-sm`}
                  placeholder="••••••••"
                  {...register('password', {
                    required: 'Le mot de passe est requis',
                    minLength: {
                      value: 6,
                      message: 'Le mot de passe doit contenir au moins 6 caractères',
                    },
                  })}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-[#0066FF] focus:ring-[#0066FF]/20 border-neutral-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-neutral-900">
                  Se souvenir de moi
                </label>
              </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-[#0066FF] hover:text-[#0066FF]">
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={loading}
              leftIcon={<LogIn className="h-5 w-5" />}
            >
              Connexion administrateur
            </Button>
          </form>

          <div className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-[#0066FF]/60" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-[#0052CC]">
                  Accès restreint
                </h3>
                <p className="mt-2 text-sm text-[#0052CC]">
                  Cette interface est réservée aux administrateurs du support Autoquote. Si vous êtes client et avez besoin d'assistance, veuillez utiliser le portail client ou contacter notre équipe support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;